'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '../../../lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  business_name: string;
  business_type: string;
  phone: string;
  county: string;
  postcode: string;
  membership_tier: string;
  cph_number: string;
  cph_verified: boolean;
  email_verified: boolean;
  is_admin: boolean;
  created_at: string;
}

const TABS = ['All Users', 'Pending Verification', 'Admins'] as const;
type Tab = typeof TABS[number];

export default function AdminConsoleScreen() {
  const { user, profile, loading } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('All Users');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setFetching(true);
    setError(null);
    try {
      // admin_list_members() rather than a table select: member phone/email are
      // no longer column-readable by `authenticated`, and this function checks
      // is_admin() before handing them over.
      const { data, error: fetchError } = await supabase.rpc('admin_list_members');

      if (fetchError) {
        setError(fetchError.message);
      } else {
        setUsers(data || []);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        router.replace('/sign-up-login-screen');
        return;
      }
      if (profile && !profile.is_admin) {
        router.replace('/');
        return;
      }
      if (profile?.is_admin) {
        fetchUsers();
      }
    }
  }, [user, profile, loading]);

  // Both of these go through functions rather than updating the column directly.
  // is_admin and cph_verified used to be writable by the `authenticated` role,
  // which meant any member could PATCH their own profile and become a site
  // administrator. See the lock_profile_privilege_columns migration.
  //
  // The errors are surfaced now too: they were being swallowed, so a failed
  // toggle looked exactly like a button that did nothing.
  const toggleAdmin = async (userId: string, currentValue: boolean) => {
    setActionLoading(userId);
    setError(null);
    const { error: rpcError } = await supabase.rpc('admin_set_member_admin', {
      member: userId,
      make_admin: !currentValue,
    });
    if (rpcError) setError(rpcError.message);
    else setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: !currentValue } : u));
    setActionLoading(null);
  };

  const toggleCphVerified = async (userId: string, currentValue: boolean) => {
    setActionLoading(`cph-${userId}`);
    setError(null);
    const { error: rpcError } = await supabase.rpc('admin_set_member_verified', {
      member: userId,
      verified: !currentValue,
    });
    if (rpcError) setError(rpcError.message);
    else setUsers(prev => prev.map(u => u.id === userId ? { ...u, cph_verified: !currentValue } : u));
    setActionLoading(null);
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      !search ||
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.business_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.county?.toLowerCase().includes(search.toLowerCase());

    if (activeTab === 'Pending Verification') return matchesSearch && !u.email_verified;
    if (activeTab === 'Admins') return matchesSearch && u.is_admin;
    return matchesSearch;
  });

  const stats = {
    total: users.length,
    verified: users.filter(u => u.email_verified).length,
    pending: users.filter(u => !u.email_verified).length,
    admins: users.filter(u => u.is_admin).length,
  };

  if (loading || (user && !profile)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Icon name="ArrowPathIcon" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!user || (profile && !profile.is_admin)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="ShieldExclamationIcon" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">You do not have permission to view this page.</p>
          <Link href="/" className="btn-primary px-6 py-2">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon name="ShieldCheckIcon" size={24} />
          <div>
            <h1 className="text-lg font-extrabold tracking-tight">Admin Console</h1>
            <p className="text-xs text-green-300">FarmTrade Direct — User Management</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-green-200 hidden sm:block">{profile?.email}</span>
          <Link href="/admin/listings" className="flex items-center gap-1.5 bg-amber-500/25 hover:bg-amber-500/40 text-amber-100 text-sm font-semibold px-3 py-2 rounded-md transition-all">
            <Icon name="ClipboardDocumentCheckIcon" size={15} />
            <span className="hidden sm:inline">Listing Review</span>
          </Link>
          <Link href="/" className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-3 py-2 rounded-md transition-all">
            <Icon name="HomeIcon" size={15} />
            <span className="hidden sm:inline">Site</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Users', value: stats.total, icon: 'UsersIcon', color: 'text-primary' },
            { label: 'Email Verified', value: stats.verified, icon: 'CheckCircleIcon', color: 'text-green-600' },
            { label: 'Pending Verification', value: stats.pending, icon: 'ClockIcon', color: 'text-amber-600' },
            { label: 'Admins', value: stats.admins, icon: 'ShieldCheckIcon', color: 'text-blue-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
              <div className={`${stat.color}`}>
                <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={24} />
              </div>
              <div>
                <p className="text-2xl font-extrabold text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search + Tabs */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex gap-1 bg-secondary rounded-lg p-1">
              {TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
                    activeTab === tab
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab}
                  {tab === 'Pending Verification' && stats.pending > 0 && (
                    <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5">{stats.pending}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-64">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="form-input pl-9 py-2 text-sm w-full"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 flex items-center gap-2 text-red-700 text-sm">
              <Icon name="ExclamationCircleIcon" size={16} />
              {error}
              <button onClick={fetchUsers} className="ml-auto text-xs underline">Retry</button>
            </div>
          )}

          {/* Table */}
          {fetching ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon name="UsersIcon" size={40} className="text-muted-foreground mb-3" />
              <p className="text-muted-foreground font-medium">No users found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or filter</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Business</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Contact</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Joined</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-secondary/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-primary">
                              {u.full_name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-foreground leading-tight">{u.full_name || '—'}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="font-medium text-foreground">{u.business_name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{u.business_type || '—'}</p>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <p className="text-foreground">{u.phone || '—'}</p>
                        <p className="text-xs text-muted-foreground">{u.county || '—'}{u.postcode ? `, ${u.postcode}` : ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${
                            u.email_verified
                              ? 'bg-green-100 text-green-700' :'bg-amber-100 text-amber-700'
                          }`}>
                            <Icon name={u.email_verified ? 'CheckCircleIcon' : 'ClockIcon'} size={11} />
                            {u.email_verified ? 'Verified' : 'Pending'}
                          </span>
                          {u.is_admin && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 w-fit">
                              <Icon name="ShieldCheckIcon" size={11} />
                              Admin
                            </span>
                          )}
                          {u.cph_verified && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 w-fit">
                              <Icon name="StarIcon" size={11} />
                              Verified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Verified-member toggle (the underlying column is still cph_verified) */}
                          <button
                            onClick={() => toggleCphVerified(u.id, u.cph_verified)}
                            disabled={actionLoading === `cph-${u.id}`}
                            title={u.cph_verified ? 'Remove verification' : 'Mark as verified'}
                            className={`p-1.5 rounded-md text-xs transition-colors disabled:opacity-50 ${
                              u.cph_verified
                                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' :'bg-secondary text-muted-foreground hover:bg-purple-100 hover:text-purple-700'
                            }`}
                          >
                            {actionLoading === `cph-${u.id}` ? (
                              <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                            ) : (
                              <Icon name="StarIcon" size={14} />
                            )}
                          </button>
                          {/* Admin toggle — prevent removing own admin */}
                          {u.id !== user?.id && (
                            <button
                              onClick={() => toggleAdmin(u.id, u.is_admin)}
                              disabled={actionLoading === u.id}
                              title={u.is_admin ? 'Remove admin' : 'Make admin'}
                              className={`p-1.5 rounded-md text-xs transition-colors disabled:opacity-50 ${
                                u.is_admin
                                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :'bg-secondary text-muted-foreground hover:bg-blue-100 hover:text-blue-700'
                              }`}
                            >
                              {actionLoading === u.id ? (
                                <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                              ) : (
                                <Icon name="ShieldCheckIcon" size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {!fetching && filteredUsers.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-secondary/30 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Showing <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> users
              </p>
              <button
                onClick={fetchUsers}
                className="flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
              >
                <Icon name="ArrowPathIcon" size={13} />
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
